import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const PRIMARY_LINKS = [
  { to: '/brief',       label: '📰 Brief',        highlight: true },
  { to: '/smart-money', label: '🧠 Smart Money'                  },
  { to: '/tracker',     label: '📊 Tracker'                      },
  { to: '/gameplan',  label: '🎯 Plan'                         },
  { to: '/quiz',      label: '🎭 Money Type',  quiz: true      },
  { to: '/community', label: '💬 Community'                    },
  { to: '/about',     label: 'About'                           },
];

const LEARN_LINKS = [
  { to: '/blog',      label: '📚 Guides',    desc: 'Plain-English money guides' },
  { to: '/courses',   label: '🎓 Courses',   desc: 'Structured finance lessons' },
  { to: '/howto',     label: '🔧 How-To',    desc: 'Step-by-step account guides' },
  { to: '/playbooks', label: '🗺️ Playbooks', desc: 'Month-by-month money roadmap' },
  { to: '/glossary',  label: '📖 Glossary',  desc: '51 financial terms explained' },
];

const TOOLS_LINKS = [
  { to: '/market-pulse',  label: '⚡ Market Pulse',    desc: 'Breaking headlines, live & auto-refreshing', highlight: true },
  { to: '/market',        label: '📈 Markets',        desc: 'Live prices & explanations' },
  { to: '/crypto-corner', label: '₿ Crypto Corner',   desc: 'Live crypto, tokenization & news' },
  { to: '/blog?tab=data', label: '📊 Data & Tools',   desc: 'Benchmarks, IRS limits, returns' },
  { to: '/healthscore',   label: '💪 Health Score',   desc: 'Rate your financial fitness' },
  { to: '/calculators',   label: '🧮 Calculators',    desc: 'Retirement, debt, investment' },
  { to: '/ask',           label: '🤖 Ask AI',         desc: 'Chat with your money coach' },
];

const ResponsiveStyle = () => (
  <style>{`
    @media (max-width: 820px) {
      .kf-desktop-links { display: none !important; }
      .kf-hamburger     { display: flex !important; }
      .kf-login-link    { display: none !important; }
      .kf-cta-btn       { display: none !important; }
    }
    @media (min-width: 821px) {
      .kf-drawer { display: none !important; }
    }
    .kf-desktop-links { display: flex; }
    .kf-hamburger { display: none; flex-direction: column; gap: 5px; background: transparent; border: none; cursor: pointer; padding: 6px; }
    .kf-dropdown:hover .kf-dropdown-menu { display: block; }
    .kf-nav-link:hover { color: #f1f5f9 !important; background: rgba(56,189,248,0.07) !important; }
    .kf-dropdown-item:hover { background: rgba(56,189,248,0.08) !important; }
  `}</style>
);

function Dropdown({ label, links, isActive }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const anyActive = links.some(l => location.pathname === l.to.split('?')[0]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          ...s.link,
          ...(anyActive ? s.active : {}),
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
        className="kf-nav-link"
      >
        {label}
        <span style={{ fontSize: '9px', opacity: 0.6, marginTop: '1px', transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
      </button>

      {open && (
        <div style={s.dropdown}>
          {links.map(({ to, label: lLabel, desc }) => (
            <Link
              key={to}
              to={to}
              style={{
                ...s.dropItem,
                ...(location.pathname === to.split('?')[0] ? s.dropItemActive : {}),
              }}
              className="kf-dropdown-item"
              onClick={() => setOpen(false)}
            >
              <div style={s.dropLabel}>{lLabel}</div>
              <div style={s.dropDesc}>{desc}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function NavbarWrapper() {
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser]         = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    const readUser = () => {
      const saved = localStorage.getItem('knowfi_user');
      if (saved) { try { setUser(JSON.parse(saved)); } catch {} } else { setUser(null); }
    };
    readUser();
    window.addEventListener('storage', readUser);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('storage', readUser); };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setOpenSection(null);
    const saved = localStorage.getItem('knowfi_user');
    if (saved) { try { setUser(JSON.parse(saved)); } catch {} } else { setUser(null); }
  }, [location.pathname]);

  const logout = () => {
    localStorage.removeItem('knowfi_user');
    setUser(null);
    window.location.href = '/';
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <ResponsiveStyle />
      <nav style={{ ...s.nav, ...(scrolled ? s.navScrolled : {}) }}>

        {/* Logo */}
        <Link to="/" style={s.logo}>
          <span style={s.logoMark}>
            <svg style={s.logoSvg} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 48" fill="none">
              <defs>
                <linearGradient id="bulbGrad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#38bdf8"/>
                  <stop offset="100%" stopColor="#a78bfa"/>
                </linearGradient>
              </defs>
              {/* Bulb body */}
              <path d="M20 3C12.268 3 6 9.268 6 17c0 4.8 2.4 9.05 6 11.6V33a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4.4c3.6-2.55 6-6.8 6-11.6C34 9.268 27.732 3 20 3Z" fill="white" opacity="0.95"/>
              {/* Base segments */}
              <rect x="13" y="37" width="14" height="3" rx="1.5" fill="white" opacity="0.85"/>
              <rect x="14.5" y="42" width="11" height="3" rx="1.5" fill="white" opacity="0.7"/>
              {/* Shine */}
              <ellipse cx="15" cy="13" rx="3" ry="5" fill="white" opacity="0.25" transform="rotate(-15 15 13)"/>
            </svg>
          </span>
          <span style={s.logoText}>KnowFi</span>
        </Link>

        {/* Desktop nav */}
        <div className="kf-desktop-links" style={s.desktopLinks}>

          {/* Primary links */}
          {PRIMARY_LINKS.map(({ to, label, highlight, quiz }) => (
            <Link key={to} to={to} className="kf-nav-link" style={{
              ...s.link,
              ...(isActive(to) ? s.active : {}),
              ...(highlight && !isActive(to) ? s.briefLink : {}),
              ...(quiz && !isActive(to) ? { background: 'rgba(139,92,246,0.12)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.25)', borderRadius: '8px', padding: '4px 10px' } : {}),
            }}>{label}</Link>
          ))}

          {/* Divider */}
          <span style={s.divider} />

          {/* Dropdowns */}
          <Dropdown label="Learn" links={LEARN_LINKS} />
          <Dropdown label="Tools" links={TOOLS_LINKS} />


        </div>

        {/* Right side */}
        <div style={s.rightRow}>
          <a href="https://x.com/OfficialKnowFi" target="_blank" rel="noopener noreferrer" style={s.xBtn} className="kf-x-btn" title="Follow us on X">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            <span className="kf-x-label">Follow</span>
          </a>
          {user ? (
            <>
              {user.plan !== 'pro' && (
                <Link to="/pricing" style={s.goProBtn} className="kf-cta-btn" title="Unlock all Pro features">⭐ Go Pro</Link>
              )}
              <Link to="/account" style={s.userName} className="kf-login-link">👋 {user.name}{user.plan === 'pro' ? ' ✦' : ''}</Link>
              <button onClick={logout} style={s.logoutBtn}>Log out</button>
            </>
          ) : (
            <>
              <Link to="/login"  style={s.loginLink} className="kf-login-link">Log in</Link>
              <Link to="/signup" style={s.ctaBtn}    className="kf-cta-btn">Get Started</Link>
            </>
          )}
          <button
            className="kf-hamburger"
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Toggle menu"
            style={{ flexDirection: 'column', gap: '5px', background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px' }}
          >
            <span style={{ ...s.bar, ...(menuOpen ? s.barTop : {}) }} />
            <span style={{ ...s.bar, ...(menuOpen ? s.barMid : {}) }} />
            <span style={{ ...s.bar, ...(menuOpen ? s.barBot : {}) }} />
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div className="kf-drawer" style={{ ...s.drawer, display: menuOpen ? 'block' : 'none' }}>
        <div style={s.drawerInner}>

          {/* Primary */}
          {PRIMARY_LINKS.map(({ to, label, highlight, quiz }) => (
            <Link key={to} to={to} style={{
              ...s.drawerLink,
              ...(isActive(to) ? s.drawerActive : {}),
              ...(highlight ? s.drawerHighlight : {}),
              ...(quiz && !isActive(to) ? { color: '#c4b5fd' } : {}),
            }}>{label}</Link>
          ))}

          <div style={s.drawerDivider} />

          {/* Learn section */}
          <button style={s.drawerSection} onClick={() => setOpenSection(o => o === 'learn' ? null : 'learn')}>
            📚 Learn {openSection === 'learn' ? '▲' : '▼'}
          </button>
          {openSection === 'learn' && LEARN_LINKS.map(({ to, label }) => (
            <Link key={to} to={to} style={{ ...s.drawerLink, paddingLeft: '28px', fontSize: '15px' }}>{label}</Link>
          ))}

          {/* Tools section */}
          <button style={s.drawerSection} onClick={() => setOpenSection(o => o === 'tools' ? null : 'tools')}>
            🛠️ Tools {openSection === 'tools' ? '▲' : '▼'}
          </button>
          {openSection === 'tools' && TOOLS_LINKS.map(({ to, label }) => (
            <Link key={to} to={to} style={{ ...s.drawerLink, paddingLeft: '28px', fontSize: '15px' }}>{label}</Link>
          ))}

          <div style={s.drawerDivider} />
          <a href="https://x.com/OfficialKnowFi" target="_blank" rel="noopener noreferrer" style={s.drawerXLink}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{marginRight:'8px'}}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            Follow @OfficialKnowFi on X
          </a>

          <div style={s.drawerDivider} />
          {user ? (
            <>
              <Link to="/account" style={{ ...s.drawerUser, textDecoration: 'none' }} onClick={() => setMenuOpen(false)}>👋 {user.name} · My Account</Link>
              <button onClick={logout} style={s.drawerLogout}>Log out</button>
            </>
          ) : (
            <>
              <Link to="/login"  style={s.drawerLogin}>Log in</Link>
              <Link to="/signup" style={s.drawerCta}>Get Started Free →</Link>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default NavbarWrapper;

const s = {
  nav: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 28px',
    background: 'rgba(15,23,42,0.88)',
    backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
    borderBottom: '1px solid transparent',
    position: 'sticky', top: 0, zIndex: 200,
    transition: 'all 0.2s', gap: '12px',
  },
  navScrolled: { borderBottom: '1px solid #1e293b', background: 'rgba(15,23,42,0.97)' },

  logo:     { textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
  logoMark: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '7px', background: 'linear-gradient(135deg, #38bdf8 0%, #a78bfa 100%)', boxShadow: '0 0 12px rgba(56,189,248,0.4), 0 2px 6px rgba(167,139,250,0.25)', flexShrink: 0 },
  logoSvg:  { width: '16px', height: '20px', display: 'block', flexShrink: 0 },
  logoText: { fontSize: '20px', fontWeight: '900', letterSpacing: '-0.5px', background: 'linear-gradient(135deg, #38bdf8 0%, #a78bfa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' },

  desktopLinks: { display: 'flex', gap: '2px', alignItems: 'center', flex: 1, justifyContent: 'center' },
  link:         { color: '#64748b', textDecoration: 'none', fontSize: '13px', fontWeight: '500', padding: '6px 10px', borderRadius: '8px', whiteSpace: 'nowrap', transition: 'all 0.15s' },
  active:       { color: '#f1f5f9', background: 'rgba(56,189,248,0.1)' },
  briefLink:    { color: '#f59e0b', fontWeight: '700' },
  divider:      { width: '1px', height: '18px', background: '#1e293b', margin: '0 6px' },

  // Dropdown menu
  dropdown:      { position: 'absolute', top: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)', background: '#1e293b', border: '1px solid #334155', borderRadius: '14px', padding: '8px', minWidth: '220px', boxShadow: '0 16px 40px rgba(0,0,0,0.4)', zIndex: 300 },
  dropItem:      { display: 'block', textDecoration: 'none', padding: '10px 12px', borderRadius: '8px', transition: 'background 0.15s' },
  dropItemActive:{ background: 'rgba(56,189,248,0.1)' },
  dropLabel:     { color: '#f1f5f9', fontSize: '13px', fontWeight: '600', marginBottom: '2px' },
  dropDesc:      { color: '#475569', fontSize: '11px' },

  rightRow:  { display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 },
  xBtn:      { display: 'flex', alignItems: 'center', gap: '5px', color: '#94a3b8', textDecoration: 'none', fontSize: '12px', fontWeight: '600', padding: '6px 10px', borderRadius: '8px', border: '1px solid #334155', whiteSpace: 'nowrap', transition: 'all 0.15s' },
  drawerXLink: { display: 'flex', alignItems: 'center', color: '#94a3b8', textDecoration: 'none', fontSize: '15px', fontWeight: '500', padding: '14px 16px', borderRadius: '10px' },
  loginLink: { color: '#94a3b8', textDecoration: 'none', fontSize: '13px', fontWeight: '500', padding: '6px 10px', whiteSpace: 'nowrap' },
  ctaBtn:    { background: '#38bdf8', color: '#0f172a', padding: '8px 16px', borderRadius: '8px', fontWeight: '700', fontSize: '13px', textDecoration: 'none', whiteSpace: 'nowrap' },
  goProBtn:  { background: 'linear-gradient(135deg, #38bdf8, #a78bfa)', color: '#fff', padding: '7px 14px', borderRadius: '8px', fontWeight: '700', fontSize: '13px', textDecoration: 'none', whiteSpace: 'nowrap', boxShadow: '0 0 12px rgba(167,139,250,0.3)' },
  userName:  { color: '#94a3b8', fontSize: '13px', whiteSpace: 'nowrap', textDecoration: 'none' },
  logoutBtn: { background: 'transparent', border: '1px solid #334155', color: '#64748b', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' },

  bar:    { width: '22px', height: '2px', background: '#94a3b8', borderRadius: '2px', transition: 'all 0.2s', display: 'block' },
  barTop: { transform: 'translateY(7px) rotate(45deg)' },
  barMid: { opacity: 0 },
  barBot: { transform: 'translateY(-7px) rotate(-45deg)' },

  drawer:      { position: 'fixed', top: '57px', left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.98)', backdropFilter: 'blur(14px)', zIndex: 199, overflowY: 'auto' },
  drawerInner: { padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '4px' },
  drawerLink:  { color: '#94a3b8', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '14px 16px', borderRadius: '10px', display: 'block' },
  drawerActive:    { color: '#f1f5f9', background: 'rgba(56,189,248,0.1)' },
  drawerHighlight: { color: '#f59e0b', fontWeight: '700' },
  drawerSection:   { background: 'transparent', border: 'none', color: '#64748b', fontSize: '14px', fontWeight: '700', padding: '12px 16px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', letterSpacing: '0.5px', textTransform: 'uppercase' },
  drawerDivider:   { height: '1px', background: '#1e293b', margin: '8px 0' },
  drawerUser:      { color: '#64748b', fontSize: '14px', padding: '8px 16px' },
  drawerLogout:    { background: 'transparent', border: '1px solid #334155', color: '#64748b', padding: '12px 16px', borderRadius: '10px', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' },
  drawerLogin:     { color: '#94a3b8', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '14px 16px', borderRadius: '10px', display: 'block' },
  drawerCta:       { background: '#38bdf8', color: '#0f172a', padding: '14px 16px', borderRadius: '10px', fontWeight: '700', fontSize: '16px', textDecoration: 'none', textAlign: 'center', marginTop: '8px', display: 'block' },
};
